import React, { useEffect, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { Button } from '@mui/material';
import { AppResultInfo, AppSkinProps, ResultTemplates } from '../../../../types/decisions';
import { getFieldName, transformCriteriaData } from '../../../../utils';
import FavoriteWithCenteredNumber from '../../../favorite-with-number/FavoriteWithNumber';
import ConfirmModal from '../../../ConfirmModal/ConfirmModal';
import { chooseTreatment, getPatientInfo } from '../../../../api/patient.api';
import { useAuth } from '../../../../context/auth-context';
import { useError } from '../../../../context/error-context';
import {
    AppResultsProps,
    getRankLabel,
    ResultProductCardSpecs,
    ResultsPagination,
    useResultActivityTracking,
} from '../AppResultsShared/AppResultsShared';
import './AppResults.scss';

type ProductAction = {
    productId: string;
    name: string;
    link: string;
};

const ProductResults = ({
    results,
    onRerank,
    onRerankMany,
    currentPage,
    shouldShowRankByManyCheckbox = false,
    selectedProductIds,
    setSelectedProductIds,
    allowedFieldsIds,
    criteriaData,
    criteriaConstraints,
    chosenProductId,
}: AppResultsProps): React.JSX.Element => {
    const { showError } = useError();
    const [ searchParams ] = useSearchParams();
    const dataset = searchParams.get('dataset') || 'main';
    const [ selectedAction ] = useState<ProductAction | null>(null);
    const [ confirmModalOpen, setConfirmModalOpen ] = useState(false);
    const [ treatmentSelectionLoading, setTreatmentSelectionLoading ] = useState(false);
    const user = JSON.parse(
        localStorage.getItem('user') || '{"isOrganic":false}',
    );
    const isOrganicUser = user.isOrganic || false;
    const shouldHideScore = criteriaData?.config.showScore === false;
    const { onProductLinkClick, onProductActionClick } = useResultActivityTracking(criteriaData);

    const onProductActionConfirm = () => {
        if (!selectedAction || !criteriaData || !criteriaConstraints) {
            setConfirmModalOpen(false);

            return;
        }

        const { criteria, constraints } = transformCriteriaData(
            criteriaData,
            criteriaConstraints,
        );

        setTreatmentSelectionLoading(true);

        chooseTreatment({
            appId: criteriaData.id,
            datasetId: dataset,
            treatmentId: selectedAction.productId,
            criteria,
            constraints,
        })
        .then(() => {
            setConfirmModalOpen(false);
            if (isOrganicUser) {
                return;
            }
            window.open(selectedAction.link, '_blank');
            window.location.reload();
        })

        .catch((err) => {
            showError({
                code: err.response?.status || 500,
                message: 'API Error',
                description: 'Failed to fetch data',
                showRefresh: true,
            });
        })
        .finally(() => setTreatmentSelectionLoading(false));
    };

    return (
        <>
            {results.map((result, index) => {
                const name = getFieldName(result.data.name);
                const productLink = result.data.productlink?.value;
                const actionLink = result.data.actionlink?.value;
                const actionCTAName = result.data.actionbuttontext?.value;
                let shouldDisplayInferButton = true;

                if (index === 0 && currentPage === 1) {
                    shouldDisplayInferButton = false;
                }

                if (chosenProductId) {
                    shouldDisplayInferButton = false;
                }

                return (
                    <div
                        className={`new-results-item ${chosenProductId === result.id ? 'chosen-item' : ''}`}
                        key={result.id}
                    >
                        {shouldShowRankByManyCheckbox && (
                            <div
                                style={{
                                    position: 'absolute',
                                    left: 8,
                                    top: 8,
                                }}
                            >
                                <div
                                    style={{
                                        marginLeft: -3,
                                        marginTop: -3,
                                    }}
                                >
                                    <FavoriteWithCenteredNumber
                                        count={selectedProductIds.indexOf(result.id) + 1}
                                        isLiked={selectedProductIds.includes(result.id)}
                                        onClick={(isChecked) => {
                                            if (isChecked) {
                                                setSelectedProductIds([
                                                    ...selectedProductIds,
                                                    result.id,
                                                ]);
                                            } else {
                                                setSelectedProductIds(
                                                    selectedProductIds.filter((id) => id !== result.id),
                                                );
                                            }
                                        }}
                                    />
                                </div>
                            </div>
                        )}

                        <div
                            className={
                                productLink ? 'image-container product-link' : 'image-container'
                            }
                            style={{ backgroundImage: `url('${result.icon}')` }}
                            onClick={() => {
                                if (productLink) {
                                    onProductLinkClick(result.id, productLink);
                                }
                            }}
                        />

                        <div className="new-results-item-details">
                            {!shouldHideScore && (
                                <div className="new-results-item-details-rank">
                                    <div
                                        className="new-results-item-details-rank-bar"
                                        style={{ width: result.rank + '%' }}
                                    />

                                    <div
                                        className="new-results-item-details-rank-rate"
                                        title={`This product meets ${result.rank}% of the specified criteria.`}
                                    >
                                        {getRankLabel(result.rank, criteriaData?.config.scoreDisplay, criteriaData?.config.scoreRanges)}
                                    </div>
                                </div>
                            )}

                            <div>
                                <h3
                                    className={`truncate ${productLink ? 'product-link' : ''}`}
                                    title={name}
                                    onClick={() => {
                                        if (productLink) {
                                            onProductLinkClick(result.id, productLink);
                                        }
                                    }}
                                >
                                    {name}
                                </h3>

                                <ResultProductCardSpecs
                                    id={result.id}
                                    nonTechnicalFields={result.nonTechnicalFields}
                                    allowedFieldsIds={allowedFieldsIds}
                                />
                            </div>

                            <div className="new-results-item-details-ctas">
                                {shouldDisplayInferButton && (
                                    <Button
                                        className="new-results-item-details-ctas-cta"
                                        variant="outlined"
                                        onClick={() => onRerank(result.id, dataset, true)}

                                        // startIcon={<ArrowUpwardIcon sx={{ color: 'var(--app-primary, #2878d4)' }}/>}
                                    >
                                        Make me #1
                                    </Button>
                                )}

                                {actionLink && actionCTAName && !criteriaData?.disabled && (
                                    <Button
                                        className="new-results-item-details-ctas-cta action-link"
                                        variant="outlined"
                                        onClick={() =>
                                            onProductActionClick(result.id, actionLink)
                                        }
                                    >
                                        {isOrganicUser ? 'I choose this treatment' : actionCTAName}
                                    </Button>
                                )}
                            </div>
                        </div>
                    </div>
                );
            })}

            <Button
                onClick={() => onRerankMany(dataset, true)}
                style={{
                    zIndex: 9999,
                    position: 'fixed',
                    bottom: 16,
                    right: selectedProductIds.length > 0 ? 16 : -120,
                    transition: 'right 0.3s ease-in-out',
                    backgroundColor: 'white',
                }}
                variant="outlined"
            >
                Infer
            </Button>

            <ConfirmModal
                isLoading={treatmentSelectionLoading}
                open={confirmModalOpen}
                title="Are you sure you want to continue?"
                message="The treatment you selected will be shared with your doctor."
                onConfirm={onProductActionConfirm}
                onCancel={() => setConfirmModalOpen(false)}
            />
        </>
    );
};

const ProductResultsV2 = ({
    results,
    onRerank,
    onRerankMany,
    currentPage,
    shouldShowRankByManyCheckbox = false,
    selectedProductIds,
    setSelectedProductIds,
    allowedFieldsIds,
    criteriaData,
}: AppResultsProps): React.JSX.Element => {
    const { onProductLinkClick, onProductActionClick } = useResultActivityTracking(criteriaData);

    return (
        <>
            {results.map((result, index) => {
                const name = getFieldName(result.data.name);
                const productLink = result.data.productlink?.value;
                const actionLink = result.data.actionlink?.value;
                const actionCTAName = result.data.actionbuttontext?.value;
                let shouldDisplayInferButton = true;
                if (index === 0 && currentPage === 1) {
                    shouldDisplayInferButton = false;
                }

                return (
                    <div className="new-results-item" key={result.id}>
                        {shouldShowRankByManyCheckbox && (
                            <div
                                style={{
                                    position: 'absolute',
                                    left: 8,
                                    top: 8,
                                }}
                            >
                                <div
                                    style={{
                                        marginLeft: -3,
                                        marginTop: -3,
                                    }}
                                >
                                    <FavoriteWithCenteredNumber
                                        count={selectedProductIds.indexOf(result.id) + 1}
                                        isLiked={selectedProductIds.includes(result.id)}
                                        onClick={(isChecked) => {
                                            if (isChecked) {
                                                setSelectedProductIds([
                                                    ...selectedProductIds,
                                                    result.id,
                                                ]);
                                            } else {
                                                setSelectedProductIds(
                                                    selectedProductIds.filter((id) => id !== result.id),
                                                );
                                            }
                                        }}
                                    />
                                </div>
                            </div>
                        )}

                        <div className="image-container-wrap">
                            <div
                                className="image-container"
                                style={{ backgroundImage: `url('${result.icon}')` }}
                            />

                            <h3
                                className={`truncate ${productLink ? 'product-link' : ''}`}
                                title={name}
                                onClick={() => {
                                    if (productLink) {
                                        onProductLinkClick(result.id, productLink);
                                    }
                                }}
                            >
                                {name}
                            </h3>

                            <div className="new-results-item-details-ctas">
                                {shouldDisplayInferButton && (
                                    <Button
                                        className="new-results-item-details-ctas-cta"
                                        variant="outlined"
                                        // TODO fix this
                                        onClick={() => onRerank(result.id, 'main', true)}
                                        // startIcon={<ArrowUpwardIcon/>}
                                    >
                                        Make me #1
                                    </Button>
                                )}

                                {actionLink && actionCTAName && (
                                    <Button
                                        className="new-results-item-details-ctas-cta action-link"
                                        variant="outlined"
                                        onClick={() => onProductActionClick(result.id, actionLink)}
                                    >
                                        {actionCTAName}
                                    </Button>
                                )}
                            </div>
                        </div>

                        <div className="new-results-item-details">
                            <div className="new-results-item-details-rank">
                                <div
                                    className="new-results-item-details-rank-bar"
                                    style={{ width: result.rank + '%' }}
                                />

                                <div
                                    className="new-results-item-details-rank-rate"
                                    title={`This product meets ${result.rank}% of the specified criteria.`}
                                >
                                    {getRankLabel(result.rank, criteriaData?.config.scoreDisplay, criteriaData?.config.scoreRanges)}
                                </div>
                            </div>

                            <div>
                                <ResultProductCardSpecs
                                    id={result.id}
                                    nonTechnicalFields={result.nonTechnicalFields}
                                    allowedFieldsIds={allowedFieldsIds}
                                />
                            </div>
                        </div>
                    </div>
                );
            })}

            <Button
                // TODO fix this
                onClick={() => onRerankMany('main', true)}
                style={{
                    zIndex: 9999,
                    position: 'fixed',
                    bottom: 16,
                    right: selectedProductIds.length > 0 ? 16 : -100,
                    transition: 'right 0.3s ease-in-out',
                    backgroundColor: 'white',
                }}
                variant="outlined"
            >
                Infer
            </Button>
        </>
    );
};

const ThreeColumns = ({
    results,
    onRerank,
    onRerankMany,
    currentPage,
    shouldShowRankByManyCheckbox,
    selectedProductIds,
    setSelectedProductIds,
    allowedFieldsIds,
    criteriaData,
    criteriaConstraints,
    chosenProductId,
}: AppResultsProps) => {
    return (
        <div className="threeColumns">
            <ProductResults
                results={results}
                onRerank={onRerank}
                onRerankMany={onRerankMany}
                currentPage={currentPage}
                shouldShowRankByManyCheckbox={shouldShowRankByManyCheckbox}
                selectedProductIds={selectedProductIds}
                setSelectedProductIds={setSelectedProductIds}
                allowedFieldsIds={allowedFieldsIds}
                criteriaData={criteriaData}
                criteriaConstraints={criteriaConstraints}
                chosenProductId={chosenProductId}
            />
        </div>
    );
};

const LargeProductCards = ({
    results,
    onRerank,
    onRerankMany,
    currentPage,
    shouldShowRankByManyCheckbox,
    selectedProductIds,
    setSelectedProductIds,
    allowedFieldsIds,
    criteriaData,
    criteriaConstraints,
    chosenProductId,
}: AppResultsProps) => {
    return (
        <div className="largeProducts">
            <ProductResults
                results={results}
                onRerank={onRerank}
                onRerankMany={onRerankMany}
                currentPage={currentPage}
                shouldShowRankByManyCheckbox={shouldShowRankByManyCheckbox}
                selectedProductIds={selectedProductIds}
                setSelectedProductIds={setSelectedProductIds}
                allowedFieldsIds={allowedFieldsIds}
                criteriaData={criteriaData}
                criteriaConstraints={criteriaConstraints}
                chosenProductId={chosenProductId}
            />
        </div>
    );
};

const DoctorsLargeProductCards = ({
    results,
    onRerank,
    onRerankMany,
    currentPage,
    shouldShowRankByManyCheckbox,
    selectedProductIds,
    setSelectedProductIds,
    allowedFieldsIds,
    criteriaData,
}: AppResultsProps) => {
    return (
        <div className="doctorsLargeProducts">
            <ProductResultsV2
                results={results}
                onRerank={onRerank}
                onRerankMany={onRerankMany}
                currentPage={currentPage}
                shouldShowRankByManyCheckbox={shouldShowRankByManyCheckbox}
                selectedProductIds={selectedProductIds}
                setSelectedProductIds={setSelectedProductIds}
                allowedFieldsIds={allowedFieldsIds}
                criteriaData={criteriaData}
            />
        </div>
    );
};

const AppResults = ({
    pageInfo,
    results,
    onPageChange,
    onRerank,
    onRerankMany,
    shouldShowRankByManyCheckbox,
    template = '3columns',
    selectedProductIds,
    setSelectedProductIds,
    allowedFieldsIds,
    criteriaData,
    criteriaConstraints,
}: Omit<AppResultsProps, 'currentPage'> & {
    template?: ResultTemplates;
    pageInfo: AppSkinProps['pageInfo'];
    onPageChange: AppSkinProps['onPageChange'];
    criteriaData?: AppSkinProps['criteriaData'];
    criteriaConstraints?: AppSkinProps['criteriaConstraints'];
}) => {
    const { showError } = useError();
    const [ searchParams ] = useSearchParams();
    const location = useLocation();
    const dataset = searchParams.get('dataset') || 'main';
    const treatmentId = searchParams.get('treatmentId') || '0';
    const { userInfo: authUserInfo } = useAuth();
    const [ userChoice, setUserChoice ] = useState<AppResultInfo | null>(null);

    useEffect(() => {
        if (!authUserInfo) {
            return;
        }

        if (authUserInfo.role === 'patient') {
            getPatientInfo()
            .then((response) => {
                const foundResultInfo = response.find((item) => {
                    return (
                        item.appId + '' === criteriaData?.id &&
                        item.datasetId + '' === dataset &&
                        item.datasetId
                    );
                });

                if (!foundResultInfo) {
                    return;
                }

                const result = results.find(
                    (item) => item.id === foundResultInfo.treatmentId,
                );

                if (!result) {
                    return;
                }
                setUserChoice(result);
            })
            .catch((err) => {
                showError({
                    code: err.response?.status || 500,
                    message: 'API Error',
                    description: 'Failed to fetch data',
                    showRefresh: true,
                });
            });
        } else if ([ 'doctor', 'manager' ].includes(authUserInfo.role)) {
            const result = results.find((item) => item.id === treatmentId);

            if (!result) {
                return;
            }
            setUserChoice(result);
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ criteriaData?.id, dataset, results ]);

    if (results.length === 0) {
        return <div className="no-results">No products found.</div>;
    }

    let ResultTemplate = ThreeColumns;

    if (template === 'largeProducts') {
        ResultTemplate = LargeProductCards;
    }

    const showPatientChoice = location.pathname.startsWith('/patient-choice');

    if (template === 'doctorsLargeProducts') {
        ResultTemplate = DoctorsLargeProductCards;
    }

    return (
        <div className="new-app-results-container">
            <h3>
                <span>Results ({pageInfo.total})</span>
                {userChoice && showPatientChoice && (
                    <span className="user-choice">
            Your choice is <span>{getFieldName(userChoice.data.name)}</span>
          </span>
                )}
            </h3>

            <div className="new-app-results">
                <ResultTemplate
                    results={results}
                    onRerank={onRerank}
                    onRerankMany={onRerankMany}
                    currentPage={pageInfo.current_page}
                    shouldShowRankByManyCheckbox={shouldShowRankByManyCheckbox}
                    selectedProductIds={selectedProductIds}
                    setSelectedProductIds={setSelectedProductIds}
                    allowedFieldsIds={allowedFieldsIds}
                    criteriaData={criteriaData}
                    criteriaConstraints={criteriaConstraints}
                    chosenProductId={showPatientChoice ? userChoice?.id : ''}
                />
            </div>

            <ResultsPagination pageInfo={pageInfo} dataset={dataset} onPageChange={onPageChange}/>
        </div>
    );
};

export default AppResults;
